<template>
  <header>
    <h1 class="page-name">Exported Files</h1>
    <p class="page-description">
      <template v-if="isExportedLogsLoading">
        <SPALoadingShimmer class="loader" />
      </template>
      <template v-else>
        <span v-if="noLogs"> No file to download </span>
        <template v-else> Choose a <SPALozenge text="READY" variant="success" disabled /> file then click <span class="SproutIcon SproutIcon-save-solid icon-download"></span> to download. </template>
      </template>
    </p>
  </header>
  <section :class="{ empty: isNoItem }">
    <template v-if="isNoItem">
      <div class="no-item">
        <template v-if="isExportedLogsLoading">
          <MDBSpinner />
        </template>
        <template v-else>
          <div class="initial-content">
            <img src="@/assets/images/2024/no-data.png" alt="no-data" />
            <h6>There are no exported items to show on your end.</h6>
            <p>Search for the logs and click <span>Export</span> to generate a file here.</p>
          </div>
        </template>
      </div>
    </template>
    <template v-else>
      <MDBTable>
        <thead>
          <tr>
            <th scope="col">FILE NAME</th>
            <th scope="col">CREATED DATE</th>
            <th scope="col">EXPORTED DATE</th>
            <th scope="col">EXPIRY DATE</th>
            <th scope="col"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(logs, key) in exportedLogs" :key="key" :class="rowClass(logs.status, logs.dateExpired)">
            <td>
              <div class="file-name">
                <MDBIcon class="icon" icon="file" iconStyle="fas" />
                <span>{{ logs.name }}</span>
              </div>
            </td>
            <td>
              <div class="date-name">
                <span>{{ parseDate(logs.dateRequested) }}</span>
                <span>{{ parseTimeFromDate(logs.dateRequested) }}</span>
              </div>
            </td>
            <td>
              <div class="date-name">
                <template v-if="logs.status === ReportStatusesEnum.InProgress">
                  <MDBSpinner class="icon" size="sm" />
                </template>
                <template v-else>
                  <span>{{ parseDate(logs.dateCompleted) }}</span>
                  <span>{{ parseTimeFromDate(logs.dateCompleted) }}</span>
                </template>
              </div>
            </td>
            <td>
              <div class="date-name">
                <template v-if="logs.status === ReportStatusesEnum.InProgress">
                  <MDBSpinner class="icon" size="sm" />
                </template>
                <template v-else>
                  <span>{{ parseDate(logs.dateExpired) }}</span>
                  <span>{{ parseTimeFromDate(logs.dateExpired) }}</span>
                </template>
              </div>
            </td>
            <td class="text-end">
              <div class="status">
                <template v-if="logs.status === ReportStatusesEnum.Ready">
                  <template v-if="isLogExpired(logs.dateExpired)">
                    <SPALozenge text="EXPIRED" disabled />
                  </template>
                  <template v-else>
                    <SPALozenge text="READY" variant="success" disabled />
                    <span class="SproutIcon SproutIcon-success-solid icon-ready"></span>
                  </template>
                </template>
                <template v-if="logs.status === ReportStatusesEnum.InProgress">
                  <SPALozenge text="EXPORTING" variant="info" disabled />
                  <MDBSpinner class="icon" size="sm" />
                </template>
                <template v-if="logs.status === ReportStatusesEnum.Faulted">
                  <SPALozenge text="ERROR" variant="error" disabled />
                </template>
              </div>
              <div class="download">
                <span class="SproutIcon SproutIcon-save-solid icon-download" @click="openModal(logs.id, logs.name)"></span>
              </div>
            </td>
          </tr>
        </tbody>
      </MDBTable>
    </template>
    <SPAPagination class="pagination" :pagination="exportedLogsPagination" @changePage="changePage" textPerPage="Files per page" />
  </section>
  <ReportDownloadModal v-model="modal" :name="openModalInfo.name" @onClose="modal = false" @download="downloadReport" />
</template>
<style lang="scss" scoped>
header {
  color: $neutral-800;
  .page-name {
    font-weight: 500;
    font-size: 24px;
    line-height: 36px;
    margin-bottom: 0;
  }
  .page-description {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 0;
    font-size: 16px;
    line-height: 24px;

    .loader {
      width: 200px;
    }

    .icon-download {
      color: $green-300;
    }
  }
}
section {
  display: flex;
  flex-direction: column;
  margin-top: 24px;
  color: $neutral-800;
  border: 1px solid $neutral-20;
  border-radius: 0.5rem;
  font-size: 14px;
  line-height: 16px;
  margin-bottom: auto;
  &.empty {
    margin-bottom: unset;
  }
  .no-item {
    display: grid;
    place-content: center;
    height: 100%;
    .initial-content {
      display: flex;
      flex-direction: column;
      align-items: center;

      h6 {
        font-weight: 500;
        font-size: 28px;
        line-height: 36px;
        margin: 0;
        color: $neutral-800;
      }
      p {
        margin-bottom: 0;
        font-size: 16px;
        line-height: 20px;

        span {
          color: $green-300;
        }
      }
    }
  }
  table {
    margin-bottom: 0;

    th {
      font-size: 12px;
      font-weight: 500;
      color: $neutral-800;
      &:not(:first-child) {
        width: 140px;
      }
    }
    td {
      padding-block: 8px;
      vertical-align: middle;
    }
    th,
    td {
      padding-inline: 16px;
    }
    .file-name {
      display: flex;
      gap: 8px;
      color: $neutral-800;
      .icon {
        color: $blue-300;
      }
    }
    .date-name {
      display: flex;
      flex-direction: column;
      color: $neutral-800;

      span:last-child {
        font-size: 12px;
        line-height: 16px;
        color: $neutral-400;
      }
    }
    .status {
      display: inline-flex;
      align-items: center;
      gap: 12px;
      .icon-ready {
        color: $green-300;
        font-size: 16px;
      }
    }
    .download {
      display: none;

      .icon-download {
        color: $green-300;
        font-size: 16px;
        cursor: pointer;
      }
    }

    .row-ready {
      &:hover {
        background-color: $green-50;
        .status {
          display: none;
        }
        .download {
          display: inline-flex;
          align-items: center;
          justify-content: end;
        }
      }
    }
    .row-exporting {
      pointer-events: none;
      .file-name {
        color: $neutral-80;
      }
      .icon {
        color: $blue-300;
      }
    }
    .row-disabled {
      pointer-events: none;
      color: $neutral-80;
      .icon,
      .date-name span {
        color: $neutral-80;
      }
    }
  }

  .pagination {
    border-top: 1px solid $neutral-20;
  }
}
</style>
<script>
import { computed, reactive, ref, onMounted } from 'vue';
import { MDBTable, MDBIcon, MDBSpinner } from 'mdb-vue-ui-kit';
import { storeToRefs } from 'pinia';
import SPALozenge from '../../../../components/SPALozenge.vue';
import SPALoadingShimmer from '../../../../components/SPALoadingShimmer.vue';
import SPAPagination from '../../../../components/SPAPagination.vue';
import { parseDate, parseTimeFromDate, isDateExpired } from '../../../../helpers/dateParser';
import { useProjectTaggingStore } from '../../../../stores/projectTagging';
import { ReportStatusesEnum } from '../../../../enums/reportStatuses';
import { useReportStore } from '../../../../stores/report';
import ReportDownloadModal from '../../../Report/ReportDownloadModal.vue';
export default {
  name: 'ProjectTaggingMainReportExport',
  components: { MDBTable, MDBIcon, MDBSpinner, SPALoadingShimmer, SPAPagination, SPALozenge, ReportDownloadModal },
  setup() {
    const useProjectTagging = useProjectTaggingStore();
    const reportStore = useReportStore();
    const { exportedLogs, exportedLogsPagination, isExportedLogsLoading, isExportedLogsInitialState } = storeToRefs(useProjectTagging);
    const changePage = async (paginate) => {
      const params = {
        PageNumber: paginate.currentPage,
        RowsPerPage: paginate.itemsPerPage,
      };
      await useProjectTagging.getExportedProjectTagging(params);
    };
    const noLogs = computed(() => {
      return exportedLogs.value.length === 0;
    });
    const isNoItem = computed(() => isExportedLogsLoading.value || isExportedLogsInitialState.value || noLogs.value);
    const rowClass = (status, dateExpired) => {
      if (status === ReportStatusesEnum.InProgress) {
        return 'row-exporting';
      }
      if (status === ReportStatusesEnum.Faulted || isDateExpired(dateExpired)) {
        return 'row-disabled';
      }
      return 'row-ready';
    };
    const modal = ref(false);
    const openModalInfo = reactive({ id: null, name: '' });
    const openModal = (id, name) => {
      openModalInfo.id = id;
      openModalInfo.name = name;
      modal.value = true;
    };
    const downloadReport = async () => {
      const url = `/api/reports/${openModalInfo.id}`;
      window.location.href = url;
      modal.value = false;
      reportStore.toggleSnackbar();
    };
    const isLogExpired = (dateExpired) => {
      return isDateExpired(dateExpired);
    };
    onMounted(async () => {
      await useProjectTagging.getExportedProjectTagging({
        PageNumber: 1,
        RowsPerPage: 10,
      });
    });
    return {
      exportedLogs,
      exportedLogsPagination,
      isExportedLogsLoading,
      noLogs,
      ReportStatusesEnum,
      isNoItem,
      isExportedLogsInitialState,
      modal,
      openModalInfo,
      rowClass,
      changePage,
      parseDate,
      parseTimeFromDate,
      downloadReport,
      openModal,
      isLogExpired,
    };
  },
};
</script>
